/**
 * INKAR API client
 */

import axios from 'axios'
import { attachAuth } from '../../lib/apiAuth'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

const api = axios.create({
  baseURL: `${API_BASE}/admin/inkar`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Types
// ============================================================================

export interface InkarFileInfo {
  filename: string
  path: string
  size_mb: number
  size_display: string
}

export interface InkarIndicatorSummary {
  kuerzel: string
  name: string
  bereich: string
  years: number[]
  year_count: number
  min_year: number
  max_year: number
  raumbezuege: string[]
  has_rs_equivalent: boolean
  rs_equivalent_code: string | null
}

export interface InkarAnalyzeResponse {
  file_path: string
  total_rows: number
  indicator_count: number
  indicators: InkarIndicatorSummary[]
  all_years: number[]
  all_raumbezuege: string[]
  bereiche: string[]
  from_cache: boolean
  cache_key: string | null
}

export interface InkarIndicatorDbItem {
  id: number
  code: string
  name: string
  kuerzel: string
  bereich: string
  description: string | null
  has_rs_equivalent: boolean
}

export interface InkarIndicatorInfo {
  code: string
  name: string
  unit: string | null
  kuerzel: string
  bereich: string
  category: string | null
  subcategory: string | null
}

export interface InkarDataSourceInfo {
  id: number
  dataset_name: string
  import_file: string | null
  reference_year: number
  import_date: string | null
  records_imported: number
  is_latest: boolean
  indicators: InkarIndicatorInfo[]
  indicator_count: number
  years_imported: number[]
  entity_levels: string[]
  entity_counts: Record<string, number>
  parent_edition_id: number | null
  parent_edition_name: string | null
}

export interface GV100EditionInfo {
  edition: string          // Edition name (e.g., "2025")
  reference_year: number   // Gebietsstand year = INKAR year (e.g., 2024)
}

export interface InkarImportPreviewResponse {
  total_datapoints: number
  indicators_count: number
  years_count: number
  estimated_new: number
  estimated_update: number
}

export interface InkarImportExecuteRequest {
  file_path: string
  indicators: Record<string, number[]>  // Kuerzel -> years
  reference_year: number
  force_update?: boolean
}

export interface InkarImportResponse {
  job_id: string
  status: string
  message: string
}

export interface InkarImportStatusResponse {
  job_id: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  message: string
  result: InkarImportResult | null
}

export interface InkarImportResult {
  success: boolean
  total_records: number
  created: number
  updated: number
  skipped: number
  errors: number
  error_messages: string[]
  duration_seconds: number
  data_source_id: number | null
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * List available INKAR CSV files
 */
export async function listSources(): Promise<InkarFileInfo[]> {
  const response = await api.get<InkarFileInfo[]>('/sources')
  return response.data
}

/**
 * Analyze a CSV file for available indicators
 */
export async function analyzeCSV(filePath: string): Promise<InkarAnalyzeResponse> {
  const response = await api.post<InkarAnalyzeResponse>('/analyze', {
    file_path: filePath,
  })
  return response.data
}

/**
 * List INKAR indicators from database
 */
export async function listIndicators(params?: {
  search?: string
  bereich?: string
  hide_rs_duplicates?: boolean
}): Promise<InkarIndicatorDbItem[]> {
  const queryParams = new URLSearchParams()
  if (params?.search) queryParams.set('search', params.search)
  if (params?.bereich) queryParams.set('bereich', params.bereich)
  if (params?.hide_rs_duplicates !== undefined) {
    queryParams.set('hide_rs_duplicates', params.hide_rs_duplicates.toString())
  }

  const queryString = queryParams.toString()
  const url = queryString ? `/indicators?${queryString}` : '/indicators'

  const response = await api.get<InkarIndicatorDbItem[]>(url)
  return response.data
}

/**
 * List imported INKAR data sources
 */
export async function listDataSources(): Promise<InkarDataSourceInfo[]> {
  const response = await api.get<InkarDataSourceInfo[]>('/data-sources')
  return response.data
}

/**
 * List available GV100 editions for year mapping
 */
export async function listGV100Editions(): Promise<GV100EditionInfo[]> {
  const response = await api.get<GV100EditionInfo[]>('/editions')
  return response.data
}

/**
 * Preview import to estimate counts
 */
export async function previewImport(
  filePath: string,
  indicators: Record<string, number[]>
): Promise<InkarImportPreviewResponse> {
  const response = await api.post<InkarImportPreviewResponse>('/import/preview', {
    file_path: filePath,
    indicators,
  })
  return response.data
}

/**
 * Execute import
 */
export async function executeImport(
  request: InkarImportExecuteRequest
): Promise<InkarImportResponse> {
  const response = await api.post<InkarImportResponse>('/import/execute', request)
  return response.data
}

/**
 * Get import job status
 */
export async function getImportStatus(jobId: string): Promise<InkarImportStatusResponse> {
  const response = await api.get<InkarImportStatusResponse>(`/import/status/${jobId}`)
  return response.data
}

// ============================================================================
// Download Types
// ============================================================================

export interface InkarDownloadRequest {
  url: string
  extract?: boolean
}

export interface InkarDownloadResponse {
  job_id: string
  status: string
  message: string
}

export interface InkarDownloadStatusResponse {
  job_id: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  message: string
  filename: string | null
  extracted_path: string | null
  error: string | null
}

// ============================================================================
// Download Functions
// ============================================================================

/**
 * Start downloading INKAR data from URL
 */
export async function startDownload(request: InkarDownloadRequest): Promise<InkarDownloadResponse> {
  const response = await api.post<InkarDownloadResponse>('/download', request)
  return response.data
}

/**
 * Get download job status
 */
export async function getDownloadStatus(jobId: string): Promise<InkarDownloadStatusResponse> {
  const response = await api.get<InkarDownloadStatusResponse>(`/download/status/${jobId}`)
  return response.data
}

// ============================================================================
// Cache Management Types
// ============================================================================

export interface CacheEntryInfo {
  cache_key: string
  file_path: string
  file_hash: string
  indicator_count: number
  total_rows: number
  ttl_seconds: number
  size_bytes: number
  created_approx: string | null
}

export interface CacheStatsResponse {
  enabled: boolean
  connected: boolean
  entries: CacheEntryInfo[]
  total_entries: number
  total_size_bytes: number
}

// ============================================================================
// Cache Management Functions
// ============================================================================

/**
 * Get cache statistics and entries
 */
export async function getCacheStats(): Promise<CacheStatsResponse> {
  const response = await api.get<CacheStatsResponse>('/cache')
  return response.data
}

/**
 * Delete a specific cache entry
 */
export async function deleteCacheEntry(cacheKey: string): Promise<{ success: boolean; deleted: string }> {
  const response = await api.delete<{ success: boolean; deleted: string }>(`/cache/${encodeURIComponent(cacheKey)}`)
  return response.data
}

/**
 * Clear all INKAR cache entries
 */
export async function clearAllCache(): Promise<{ success: boolean; deleted_count: number }> {
  const response = await api.delete<{ success: boolean; deleted_count: number }>('/cache')
  return response.data
}

/**
 * Force refresh a cache entry
 */
export async function refreshCacheEntry(filePath: string): Promise<{
  success: boolean
  cache_key: string
  indicator_count: number
  total_rows: number
}> {
  const response = await api.post('/cache/refresh', { file_path: filePath })
  return response.data
}

// ============================================================================
// Raumgliederungen Types
// ============================================================================

export interface RaumgliederungenFileInfo {
  filename: string
  path: string
  reference_year: number | null
}

export interface RaumgliederungenStatus {
  reference_year: number
  files: RaumgliederungenFileInfo[]
  municipality_mappings: number
  county_mappings: number
  total_municipalities: number
  total_counties: number
  mapping_complete: boolean
}

export interface RaumgliederungenImportRequest {
  file_path: string
  reference_year: number
}

export interface RaumgliederungenImportResponse {
  success: boolean
  reference_year: number | null
  municipality_mappings_created: number
  municipality_mappings_updated: number
  county_mappings_created: number
  county_mappings_updated: number
  municipalities_not_found: number
  counties_not_found: number
  duration_seconds: number
  error_message: string | null
}

// ============================================================================
// Raumgliederungen Functions
// ============================================================================

/**
 * Get Raumgliederungen import status
 */
export async function getRaumgliederungenStatus(): Promise<RaumgliederungenStatus[]> {
  const response = await api.get<RaumgliederungenStatus[]>('/raumgliederungen/status')
  return response.data
}

/**
 * Import Raumgliederungen mappings from Excel file
 */
export async function importRaumgliederungen(
  request: RaumgliederungenImportRequest
): Promise<RaumgliederungenImportResponse> {
  const response = await api.post<RaumgliederungenImportResponse>('/raumgliederungen/import', request)
  return response.data
}

// Basic-Auth-Header + 401-Behandlung (optionaler Schutz, v0.3.8)
attachAuth(api)
